import { useMemo } from 'react'
import { BarChart3, TrendingUp, Calendar, Target } from 'lucide-react'

interface ReadingRecord {
  date: string
  count: number
  bookTitle?: string
}

interface ReadingStatsProps {
  records: ReadingRecord[]
}

export default function ReadingStats({ records }: ReadingStatsProps) {
  const stats = useMemo(() => {
    if (!records.length) {
      return {
        totalCount: 0,
        totalDays: 0,
        averagePerDay: 0,
        maxInDay: 0,
        currentStreak: 0,
        longestStreak: 0,
        thisWeek: 0,
        thisMonth: 0,
      }
    }

    const sortedRecords = [...records].sort((a, b) => a.date.localeCompare(b.date))
    const totalCount = records.reduce((sum, record) => sum + record.count, 0)
    const totalDays = records.length
    const averagePerDay = totalDays > 0 ? Math.round(totalCount / totalDays * 10) / 10 : 0
    const maxInDay = Math.max(...records.map(r => r.count))

    // 计算连续天数

    let currentStreak = 0
    let longestStreak = 0
    let tempStreak = 0

    // 从最近的日期开始计算当前连续天数
    const recordDates = new Set(records.map(r => r.date))
    let checkDate = new Date()
    
    while (true) {
      const dateStr = checkDate.toISOString().split('T')[0]
      if (recordDates.has(dateStr)) {
        currentStreak++
        checkDate.setDate(checkDate.getDate() - 1)
      } else {
        break
      }
    }

    // 计算最长连续天数
    for (let i = 0; i < sortedRecords.length; i++) {
      if (i === 0) {
        tempStreak = 1
      } else {
        const prevDate = new Date(sortedRecords[i - 1].date)
        const currDate = new Date(sortedRecords[i].date)
        const diffDays = Math.floor((currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24))
        
        if (diffDays === 1) {
          tempStreak++
        } else {
          longestStreak = Math.max(longestStreak, tempStreak)
          tempStreak = 1
        }
      }
    }
    longestStreak = Math.max(longestStreak, tempStreak)

    // 计算本周和本月数据
    const now = new Date()
    const weekStart = new Date(now.setDate(now.getDate() - now.getDay())).toISOString().split('T')[0]
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]

    const thisWeek = records
      .filter(r => r.date >= weekStart)
      .reduce((sum, r) => sum + r.count, 0)

    const thisMonth = records
      .filter(r => r.date >= monthStart)
      .reduce((sum, r) => sum + r.count, 0)



    return {
      totalCount,
      totalDays,
      averagePerDay,
      maxInDay,
      currentStreak,
      longestStreak,
      thisWeek,
      thisMonth,
    }
  }, [records])

  return (
    <div className="space-y-3">
      {/* 主要统计数据 */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-3 text-center">
          <BarChart3 className="mx-auto h-6 w-6 text-blue-600 mb-2" />
          <div className="text-lg font-semibold text-blue-600">{stats.totalCount}</div>
          <div className="text-xs text-gray-500 mt-1">总遍数</div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-3 text-center">
          <Calendar className="mx-auto h-6 w-6 text-green-600 mb-2" />
          <div className="text-lg font-semibold text-green-600">{stats.totalDays}</div>
          <div className="text-xs text-gray-500 mt-1">总天数</div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-3 text-center">
          <TrendingUp className="mx-auto h-6 w-6 text-purple-600 mb-2" />
          <div className="text-lg font-semibold text-purple-600">{stats.averagePerDay}</div>
          <div className="text-xs text-gray-500 mt-1">日均</div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-3 text-center">
          <Target className="mx-auto h-6 w-6 text-orange-600 mb-2" />
          <div className="text-lg font-semibold text-orange-600">{stats.maxInDay}</div>
          <div className="text-xs text-gray-500 mt-1">单日最高</div>
        </div>
      </div>

      {/* 连续天数和周期统计 */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-3">
          <h4 className="font-semibold text-gray-900 mb-3 text-sm">连续记录</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-xs">当前</span>
              <span className="font-semibold text-green-600 text-sm">{stats.currentStreak}天</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-xs">最长</span>
              <span className="font-semibold text-blue-600 text-sm">{stats.longestStreak}天</span>
            </div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-3">
          <h4 className="font-semibold text-gray-900 mb-3 text-sm">周期统计</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-xs">本周</span>
              <span className="font-semibold text-purple-600 text-sm">{stats.thisWeek}遍</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-xs">本月</span>
              <span className="font-semibold text-orange-600 text-sm">{stats.thisMonth}遍</span>
            </div>
          </div>
        </div>
      </div>


    </div>
  )
}
